import { useTranslation } from 'react-i18next';
import { type QueryClient, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

/**
 * Hook do pobierania unikalnych wartości danej kolumny + ich liczności.
 * Zasila popup filtra w nagłówku tabeli (tryb "lista wartości").
 *
 * `tableId` (nazywany historycznie `moduleId` w SellCall) decyduje
 * o endpointcie:
 *  - 'contractors' → /api/contractors/distinct?field=X (Krok 2.B)
 *  - 'companies'   → /api/companies/distinct?field=X
 *  - 'employees'   → /api/employees/distinct?field=X (pilot kreatora list)
 *  - 'module-<id>' → /api/modules/:id/records/distinct?field=X (lista kartoteki własnej)
 *  - 'decision-section:<zakodowany klucz>[?overdue=true][&scope=team]' (z `buildSectionDistinctTableId`) →
 *    /api/processes/my-pending-decisions/section/distinct?key=<klucz>&field=X[&overdue=true][&scope=team]
 *    — sekcja kartoteki kokpitu decyzji: wartości liczone z rekordów SAMEJ sekcji (zbiór decyzji
 *    użytkownika albo, w zakresie „Zespół", jego podwładnych), nie z całej kartoteki; kształt
 *    odpowiedzi identyczny jak `/purchase-invoices/distinct`
 *  - inny slug modułu systemowego → /api/system-modules/:slug/records/distinct
 *    (NIE wdrożone — Krok replikacji systemowych modułów)
 *
 * Gdy mapowanie nie istnieje, hook zwraca `enabled: false` (query nie odpala
 * się, brak request'a do nieistniejącego endpointu).
 */

export interface DistinctValue {
  value: string | null;
  label: string;
  count: number;
  color?: string;
  year?: number;
  month?: number;
  day?: number;
}

/** Kształt odpowiedzi endpointów podpowiedzi (`…/distinct?field=`); `truncated` = lista ucięta limitem serwera. */
export interface DistinctValuesResponse {
  values: DistinctValue[];
  total: number;
  field_type: string;
  truncated?: boolean;
}

/**
 * Pierwszy element klucza react-query podpowiedzi filtra: `['distinct-values', tableId, pole]`.
 * Eksportowany, bo inne haki wczytujące te same podpowiedzi (opcje wiersza filtrów kartoteki własnej)
 * trzymają się tego klucza — unieważnienia po prefiksie obejmują wtedy je wszystkie.
 */
export const DISTINCT_VALUES_QUERY_KEY = 'distinct-values';

/**
 * Czas świeżości podpowiedzi filtra (liczonych z rekordów): starzeją się po 30 s. Wspólny dla popupu
 * lejka w nagłówku (`useDistinctValues`) i dla haków wczytujących te same podpowiedzi na opcje
 * wiersza filtrów kartoteki własnej (`useModuleListFilterOptions`) — jedna wartość, jeden klucz cache.
 */
export const DISTINCT_VALUES_STALE_TIME_MS = 30_000;

/** Prefiks `distinctTableId` sekcji kartoteki kokpitu decyzji (patrz `buildSectionDistinctTableId`). */
const SECTION_DISTINCT_PREFIX = 'decision-section:';

/**
 * `distinctTableId` sekcji kartoteki kokpitu decyzji (`ConfigurableTable.distinctTableId`):
 * `decision-section:<klucz sekcji zakodowany encodeURIComponent>`, a w zakresie „Przeterminowane"
 * dopisek `?overdue=true`, w zakresie „Zespół" dopisek `scope=team` (oba razem: `?overdue=true&scope=team`).
 * Klucz sekcji zawiera dwukropek (`documents:purchase_invoice`), więc idzie zakodowany
 * (`documents%3Apurchase_invoice`) — dzięki temu `?` oddzielające dopiski jest jednoznaczne.
 * `buildEndpointUrl` ROZKODOWUJE ten klucz i koduje go ponownie przez `URLSearchParams` (adres nie
 * dziedziczy więc kodowania identyfikatora ani nie psuje się od niezakodowanego klucza).
 * Osobny od `tableId` tabeli sekcji (`processes-decisions:…`, klucz układu), bo wskazuje endpoint
 * podpowiedzi, i część kluczy cache `['distinct-values', …]` — zakresy „Przeterminowane" i „Zespół"
 * mają osobne podpowiedzi. `scope` domyślnie `mine` (jak dotąd: bez dopisku).
 */
export function buildSectionDistinctTableId(
  sectionKey: string,
  overdue: boolean,
  // Literał, nie typ z modułu procesów: wspólna tabela nie zależy od `lib/processes`.
  scope: 'mine' | 'team' = 'mine',
): string {
  const suffix = [overdue ? 'overdue=true' : null, scope === 'team' ? 'scope=team' : null]
    .filter((part): part is string => part !== null)
    .join('&');
  return `${SECTION_DISTINCT_PREFIX}${encodeURIComponent(sectionKey)}${suffix === '' ? '' : `?${suffix}`}`;
}

// Dev-only strażnik (deduplikowany po kluczu, jak ostrzeżenia w `buildCoreColumns`): identyfikator
// sekcji z kluczem, którego nie da się rozkodować, wyłącza podpowiedzi filtra BEZ śladu — w dev
// ostrzegamy raz na klucz. Milczy w produkcji (`import.meta.env.DEV`).
const warnedUndecodableSectionKeys = new Set<string>();
function warnUndecodableSectionKey(encodedKey: string): void {
  if (!import.meta.env.DEV || warnedUndecodableSectionKeys.has(encodedKey)) return;
  warnedUndecodableSectionKeys.add(encodedKey);
  console.warn(
    `[buildEndpointUrl] Klucz sekcji „${encodedKey}" w distinctTableId nie daje się rozkodować ` +
      `(decodeURIComponent) — podpowiedzi filtra tej sekcji są wyłączone. Buduj identyfikator ` +
      `przez buildSectionDistinctTableId.`,
  );
}

/**
 * Adres endpointu podpowiedzi dla `tableId` + pola; `null` = brak mapowania (zapytanie wyłączone).
 * Eksportowany dla testów mapowania.
 */
export function buildEndpointUrl(tableId: string, fieldSlug: string): string | null {
  if (tableId.startsWith(SECTION_DISTINCT_PREFIX)) {
    // Klucz sekcji wraca do postaci jawnej i dopiero `URLSearchParams` koduje go do adresu — dzięki
    // temu także identyfikator z niezakodowanym kluczem (np. z `&`) nie rozbije parametrów zapytania.
    // Błąd dekodowania (zepsuty `%…`) lub pusty klucz = brak mapowania (zapytanie wyłączone).
    const [encodedKey = '', query = ''] = tableId.slice(SECTION_DISTINCT_PREFIX.length).split('?');
    let key: string;
    try {
      key = decodeURIComponent(encodedKey);
    } catch {
      warnUndecodableSectionKey(encodedKey);
      return null;
    }
    if (key === '') return null;
    const params = new URLSearchParams({ key, field: fieldSlug });
    // Dopiski zakresu (`overdue=true`, `scope=team`) — tylko te dwie wartości; resztę ignorujemy.
    const suffix = new URLSearchParams(query);
    if (suffix.get('overdue') === 'true') params.set('overdue', 'true');
    if (suffix.get('scope') === 'team') params.set('scope', 'team');
    return `/processes/my-pending-decisions/section/distinct?${params.toString()}`;
  }
  if (tableId === 'contractors') {
    return `/contractors/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'companies') {
    return `/companies/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'employees') {
    return `/employees/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'users') {
    return `/users/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'contractor_contacts') {
    return `/contractor-contacts/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'purchase_invoices') {
    return `/purchase-invoices/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'sale_invoices') {
    return `/sale-invoices/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'products') {
    return `/products/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'contracts') {
    return `/contracts/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'letters') {
    return `/letters/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'commercial') {
    return `/commercial-docs/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'internal') {
    return `/internal-docs/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId === 'specjalne') {
    return `/special-docs/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  if (tableId.startsWith('module-')) {
    const moduleId = tableId.replace(/^module-/, '');
    return `/modules/${moduleId}/records/distinct?field=${encodeURIComponent(fieldSlug)}`;
  }
  // Wszystkie listy systemowe są już wpięte. Custom moduły systemowe poza listą
  // powyżej (jeśli dojdą) — dolejemy przy ich migracji na ConfigurableTable.
  return null;
}

export function useDistinctValues(
  tableId: string | undefined,
  fieldSlug: string | undefined,
  enabled: boolean,
) {
  const { t: _t } = useTranslation('common');
  const url = tableId && fieldSlug ? buildEndpointUrl(tableId, fieldSlug) : null;
  return useQuery({
    queryKey: [DISTINCT_VALUES_QUERY_KEY, tableId, fieldSlug],
    queryFn: async () => {
      const res = await api.get<DistinctValuesResponse>(url!);
      return res.data;
    },
    enabled: !!url && enabled,
    staleTime: DISTINCT_VALUES_STALE_TIME_MS,
  });
}

/**
 * Unieważnia podpowiedzi filtra WSZYSTKICH sekcji kartotek kokpitu decyzji (klucze
 * `['distinct-values', 'decision-section:…', pole]`). Wołaj po decyzji i po akcji masowej: zbiór
 * rekordów sekcji się zmienił (decydowane rekordy znikają z listy), a podpowiedzi są liczone z jej
 * rekordów — bez tego popup pokazywałby do 30 s (staleTime) wartości nieobecne już w sekcji.
 * Podpowiedzi zwykłych list kartotek zostają nietknięte.
 */
export function invalidateSectionDistinctValues(queryClient: QueryClient): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: [DISTINCT_VALUES_QUERY_KEY],
    predicate: (query) => String(query.queryKey[1]).startsWith(SECTION_DISTINCT_PREFIX),
  });
}
